"use client";

import React from "react";
import InputHolder from "@/components/forms/inputHolder";
import Textarea from "@/components/forms/textarea";
import SelectPill from "@/components/creator/SelectPill";
import { HOW_DID_YOU_HEAR } from "@/app/constants/creatorNetwork";
import type { CreatorApplication } from "@/types/creatorNetwork";

type StepProps = {
  data: CreatorApplication;
  onUpdate: (patch: Partial<CreatorApplication>) => void;
  errors: Record<string, string | null | undefined>;
};

export default function StepJoin({ data, onUpdate, errors }: StepProps) {
  return (
    <div>
      <h3 className="step-title text-white text-2xl font-bold mb-1">
        Join the community
      </h3>
      <p className="text-white/60 mb-6">
        Almost done — just a few more details before you submit.
      </p>

      <div className="mb-6">
        <Textarea
          input={{
            name: "whyJoin",
            label: "Why would you like to join?",
            placeholder: "Tell us a little about yourself and what you create...",
          }}
          value={data.whyJoin}
          onUpdate={(name: string, value: string) => onUpdate({ [name]: value })}
        />
        {errors.whyJoin && (
          <div className="mt-1 pl-4 text-red text-sm">{errors.whyJoin}</div>
        )}
      </div>

      <div className="mb-6 sm:px-4 md:w-1/2">
        <SelectPill
          name="howDidYouHearAboutUs"
          value={data.howDidYouHearAboutUs}
          onChange={(v) => onUpdate({ howDidYouHearAboutUs: v })}
          options={HOW_DID_YOU_HEAR}
          placeholder="How did you hear about us?"
        />
        {errors.howDidYouHearAboutUs && (
          <div className="mt-1 pl-4 text-red text-sm">
            {errors.howDidYouHearAboutUs}
          </div>
        )}
        {data.howDidYouHearAboutUs === "others" && (
          <input
            type="text"
            value={data.howDidYouHearAboutUsOther}
            onChange={(e) => onUpdate({ howDidYouHearAboutUsOther: e.target.value })}
            placeholder="Please specify..."
            className="!w-full mt-3"
          />
        )}
      </div>

      <div className="relative w-full mb-6 md:w-1/2">
        <InputHolder
          input={{
            inputType: "text",
            name: "referralCode",
            label: "Referral code (optional)",
          }}
          value={data.referralCode}
          onUpdate={(name: string, value: string) => onUpdate({ [name]: value })}
        />
      </div>

      <div className="flex items-start mb-3 sm:px-4">
        <input
          type="checkbox"
          id="consentContact"
          className="custom-checkbox"
          checked={data.consentContact}
          onChange={(e) => onUpdate({ consentContact: e.target.checked })}
        />
        <label
          htmlFor="consentContact"
          className="flex items-start gap-2.5 cursor-pointer"
        >
          <span className="w-4 h-4 inline-block shrink-0 mt-1"></span>
          <div className="text-white/80 text-sm">
            I agree to be contacted by Kingdom Digital about relevant brand campaigns.
          </div>
        </label>
      </div>
      {errors.consentContact && (
        <div className="mb-3 pl-4 text-red text-sm">{errors.consentContact}</div>
      )}

      <div className="flex items-start sm:px-4">
        <input
          type="checkbox"
          id="consentPdpa"
          className="custom-checkbox"
          checked={data.consentPdpa}
          onChange={(e) => onUpdate({ consentPdpa: e.target.checked })}
        />
        <label
          htmlFor="consentPdpa"
          className="flex items-start gap-2.5 cursor-pointer"
        >
          <span className="w-4 h-4 inline-block shrink-0 mt-1"></span>
          <div className="text-white/80 text-sm">
            I have read and agree to the{" "}
            <a href="/privacy" target="_blank" className="text-cyan underline">
              Privacy Policy
            </a>{" "}
            and consent to the processing of my personal data under the Personal Data
            Protection Act 2010 (PDPA).
          </div>
        </label>
      </div>
      {errors.consentPdpa && (
        <div className="pl-4 text-red text-sm">{errors.consentPdpa}</div>
      )}
    </div>
  );
}
